import type { PokemonV2 } from '../types/pokemon';
import type { POKEMON_ITEM_ID } from '../types/item';
import type { otherSetting } from '../types/other-setting';
import type { StatInputs } from '../types/stat';
import type { AttackPokemonData } from '../types/attack';
import type { ComprehensiveOpponentListData } from '../types/comprehensive';
import type { MoveDamageSetting } from '../types/damage-setting';

/**
 * PokemonSelectPageで使用するlocalStorageデータ構造
 */
export interface PokemonSelectPageData {
  selectedPokemon: PokemonV2 | null;
  statInputs: StatInputs;
  attackPokemonList: AttackPokemonData[];
  selectedDefenseItem: POKEMON_ITEM_ID;
  otherSetting: otherSetting;
  timestamp: string;
}

/**
 * useViewModelで使用するOffenceポケモンリストのlocalStorageデータ構造
 */
export interface OffencePokemonListData {
  offencePokemonList: PokemonV2[];
  timestamp: string;
}

/**
 * useViewModelで使用するDefenceポケモンのlocalStorageデータ構造
 */
export interface DefencePokemonData {
  defencePokemon: PokemonV2;
  timestamp: string;
}

/**
 * 防御モード用ポケモンリストのlocalStorageデータ構造
 */
export interface DefencePokemonListData {
  defencePokemonList: AttackPokemonData[];
  timestamp: string;
}

export type { ComprehensiveOpponentListData };

/**
 * ダメージ計算結果に付随する「追加設定UI」の保存データ
 * - 計算条件に影響するが、ポケモン本体の入力値とは別管理したいもの（急所/じゅうでん/可変威力など）
 * - 画面リロード後も設定が残るように localStorage に保存する
 */
export interface DamageSettingUiStateV1 {
  version: 1;
  timestamp: string;
  // 与えるダメージ（AttackModeDisplay）向け：表示コンテキストごとに保持する
  attackModeByKey: Record<
    string,
    {
      selectedMoveIds?: number[];
      moveSettingByMoveId?: Record<number, MoveDamageSetting>;
    }
  >;
  // 受けるダメージ（ComprehensiveMode）向け：相手横断の状態を保持する
  damageTaken: {
    selectedMoveKeys?: string[];
    moveSettingByKey?: Record<string, MoveDamageSetting>;
  };
}

/**
 * localStorageに保存可能なデータ型の制限
 */
export type StorageValue = 
  | PokemonSelectPageData 
  | OffencePokemonListData 
  | DefencePokemonData
  | DefencePokemonListData
  | ComprehensiveOpponentListData
  | DamageSettingUiStateV1
  | string 
  | number 
  | boolean 
  | null;

/**
 * localStorageのキー定数
 */
export const STORAGE_KEYS = {
  POKEMON_SELECT_PAGE_DATA: 'pokemonSelectPageData',
  POKEMON_SELECT_SHOW_SV_ONLY: 'pokemonSelectShowSvOnly',
  OFFENCE_POKEMON_LIST: 'offencePokemonList',
  DEFENCE_POKEMON: 'defencePokemon',
  DEFENCE_POKEMON_LIST: 'defencePokemonList',
  ATTACK_POKEMON_LIST: 'attackPokemonList',
  COMPREHENSIVE_OPPONENT_LIST: 'comprehensiveOpponentList',
  DAMAGE_SETTING_UI_STATE: 'damageSettingUiState',
} as const;

/**
 * StorageKeyの型定義
 */
export type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];

/**
 * エラー結果の型定義
 */
export interface StorageError {
  success: false;
  error: string;
  data?: never;
}

/**
 * 成功結果の型定義
 */
export interface StorageSuccess<T> {
  success: true;
  data: T;
  error?: never;
}

/**
 * ストレージ操作の結果型
 */
export type StorageResult<T> = StorageSuccess<T> | StorageError;

/**
 * 保存されたデータの情報
 */
export interface SavedDataInfo {
  timestamp: string | null;
  hasData: boolean;
  size?: number;
}
